import * as React from "react";
import { useCallback, useMemo } from "react";
import { styled } from "styled-components";
import { useTranslation } from "react-i18next";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import ReportQueryHelper from "report/report-history-page/ReportHistoryQueryHelper";
import { STATUS_LABELS } from "report/report-history-page/StatusCell";
import fetchReportHistory from "./fetchReportHistory";
import ReportHistoryActions from "./ReportHistoryActions";
import customColumnRenderers from "./ColumnRenderers";
const DEFAULT_DISPLAYED_ATTRIBUTES = ["generated_at", "status", "recipients", "has_asset", "message"];
const TABLE_LAYOUT = {
  defaultColumnOrder: DEFAULT_DISPLAYED_ATTRIBUTES,
  defaultDisplayedAttributes: DEFAULT_DISPLAYED_ATTRIBUTES,
  defaultPageSize: 20,
  defaultSort: { attributeId: "generated_at", direction: "desc" },
  entityTableId: "reports_history"
};
const fetchOptions = { refetchInterval: 2e3 };
const FilterValueRenderers = {
  status: (value) => {
    var _a;
    return (_a = STATUS_LABELS[value]) != null ? _a : value;
  }
};
const Table = styled.div`
  display: flex;
  gap: 50px;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
`;
const Label = styled.span`
  font-weight: 300;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;
const GrowColumn = styled(Column)`
  flex-grow: 1;
`;
const Recipient = styled.div``;
const NoRecipients = styled.i`
  white-space: nowrap;
`;
const MessageDetails = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
`;
const ReportHistoryList = ({ report, editAllowed }) => {
  const { t } = useTranslation("enterprise_report");
  const reportId = report.id;
  const additionalAttributes = useMemo(
    () => [{ id: "recipients", title: t("report_history_list.recipients"), sortable: false }],
    [t]
  );
  const expandedSections = useMemo(
    () => ({
      details: {
        title: t("report_history_list.details"),
        content: (history) => {
          var _a, _b;
          return /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement(Column, null, /* @__PURE__ */ React.createElement(Label, null, t("report_history_list.recipients")), ((_a = history.recipients) == null ? void 0 : _a.length) ? (_b = history.recipients) == null ? void 0 : _b.map((recipient) => /* @__PURE__ */ React.createElement(Recipient, { key: recipient }, recipient)) : /* @__PURE__ */ React.createElement(NoRecipients, null, t("report_history_list.no_recipients"))), /* @__PURE__ */ React.createElement(GrowColumn, null, /* @__PURE__ */ React.createElement(Label, null, t("report_history_list.message_details")), history.message ? /* @__PURE__ */ React.createElement(MessageDetails, null, history.message) : /* @__PURE__ */ React.createElement("i", null, t("message_cell.no_message"))));
        },
        disableHeader: true,
        isAvailable: () => editAllowed
      }
    }),
    [t, editAllowed]
  );
  const keyFn = useCallback((searchParams) => ["reports", "history", reportId, searchParams], [reportId]);
  const _fetchReportHistory = useCallback(
    (searchParams) => fetchReportHistory(reportId, searchParams),
    [reportId]
  );
  const entityActions = useCallback(
    (listItem) => /* @__PURE__ */ React.createElement(ReportHistoryActions, { report, reportHistory: listItem }),
    [report]
  );
  const _customColumnRenderers = useMemo(() => customColumnRenderers(reportId), [reportId]);
  return /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: t("report_history_list.human_name"),
      queryHelpComponent: /* @__PURE__ */ React.createElement(ReportQueryHelper, null),
      entityActions,
      tableLayout: TABLE_LAYOUT,
      fetchEntities: _fetchReportHistory,
      expandedSectionRenderers: expandedSections,
      fetchOptions,
      filterValueRenderers: FilterValueRenderers,
      entityAttributesAreCamelCase: false,
      keyFn,
      additionalAttributes,
      columnRenderers: _customColumnRenderers
    }
  );
};
export default ReportHistoryList;
